import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

// API URL - prázdný string pro produkci (relativní cesta), localhost pro vývoj
const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');

// Funkce pro trackování stažení
const trackDownload = async (ticketId) => {
  try {
    await fetch(`${API_URL}/api/tickets/${ticketId}/download`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });
  } catch (error) {
    console.error('Chyba při trackování stažení:', error);
  }
};

export const generateTicketPNG = async (ticket, aspectRatio = '4:5', shouldTrack = true) => {
  // Aspect ratio dimensions (width x height in pixels)
  const aspectRatios = {
    '4:5': { width: 1080, height: 1350 },   // Instagram portrait
    '1:1': { width: 1080, height: 1080 },   // Instagram square
    '9:16': { width: 1080, height: 1920 }   // Instagram story
  };

  const dimensions = aspectRatios[aspectRatio] || aspectRatios['4:5'];

  // Scaling factors pro různé aspect ratio
  const scalingFactors = {
    '4:5': 0.85,  // Zmenšit aby se vše vešlo
    '1:1': 0.65,  // Pro čtvercový formát ještě víc zmenšit
    '9:16': 1     // Story formát má dost prostoru
  };
  const scale = scalingFactors[aspectRatio] || 1;

  // Formátování data
  const formatDate = (dateString) => {
    const isoString = dateString.includes('T')
      ? dateString
      : dateString.replace(/\s+/, 'T') + 'Z';

    const date = new Date(isoString);

    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateString, 'converted to:', isoString);
      return 'Neplatné datum';
    }

    return date.toLocaleString('cs-CZ', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Prague'
    });
  };

  const getExpectedDate = () => {
    const isoString = ticket.created_at.includes('T')
      ? ticket.created_at
      : ticket.created_at.replace(/\s+/, 'T') + 'Z';
    const createdDate = new Date(isoString);

    if (isNaN(createdDate.getTime())) {
      return ticket.expected_year;
    }

    const day = createdDate.getDate();
    const month = createdDate.toLocaleString('cs-CZ', { month: 'long', timeZone: 'Europe/Prague' });

    return `${day}. ${month} ${ticket.expected_year}`;
  };

  // Dynamická velikost písma pro PNG - podle délky textu
  const getRequestFontSizePNG = (text, baseSize) => {
    const length = text.length;
    if (length <= 30) return baseSize * 1.6;
    if (length <= 50) return baseSize * 1.5;
    if (length <= 70) return baseSize * 1.4;
    if (length <= 90) return baseSize * 1.3;
    if (length <= 110) return baseSize * 1.2;
    if (length <= 130) return baseSize * 1.1;
    if (length <= 150) return baseSize * 1;
    return baseSize * 0.9;
  };


  // Vytvoření HTML elementu pro PNG
  const pngElement = document.createElement('div');
  pngElement.style.position = 'absolute';
  pngElement.style.left = '-9999px';
  pngElement.style.width = `${dimensions.width}px`;
  pngElement.style.height = `${dimensions.height}px`;
  pngElement.style.background = 'white';
  pngElement.style.padding = '0';
  pngElement.style.fontFamily = 'Arial, sans-serif';
  pngElement.style.display = 'flex';
  pngElement.style.flexDirection = 'column';

  pngElement.innerHTML = `
    <div style="width: ${dimensions.width}px; height: ${dimensions.height}px; background: white; display: flex; flex-direction: column;">
      <!-- Tělo ticketu -->
      <div style="padding: ${50 * scale}px; text-align: center; flex: 1; display: flex; flex-direction: column;">
        <!-- Číslo lístku -->
        <div style="background: #f8f9fa; border: ${4 * scale}px dashed #0056b3; border-radius: ${16 * scale}px; padding: ${30 * scale}px; margin-bottom: ${40 * scale}px; text-align: center;">
          <div style="display: block; font-size: ${32 * scale}px; color: #666; margin-bottom: ${10 * scale}px; text-transform: uppercase; letter-spacing: ${2 * scale}px; font-weight: bold;">Pořadové číslo:</div>
          <div style="display: block; font-size: ${96 * scale}px; font-weight: bold; color: #0056b3; font-family: 'Courier New', monospace; margin: 0; line-height: 1;">${'#' + ticket.ticket_number}</div>
        </div>

        <!-- Požadavek -->
        <div style="margin-bottom: ${40 * scale}px; text-align: left; flex: 1; display: flex; flex-direction: column; min-height: 0;">
          <h4 style="font-size: ${32 * scale}px; color: #666; margin: 0 0 ${20 * scale}px 0; text-transform: uppercase; font-weight: bold; letter-spacing: ${2 * scale}px;">Co potřebuji od státu:</h4>
          <div style="background: #f8f9fa; padding: ${40 * scale}px; border-radius: ${16 * scale}px; border-left: ${8 * scale}px solid #0056b3; flex: 1; display: flex; align-items: center; justify-content: center; flex-direction: column; min-height: 0; overflow: hidden;">
            <div style="margin: 0; font-size: ${getRequestFontSizePNG(ticket.request, 64) * scale}px; line-height: 1.4; color: #333; font-weight: 600; text-align: left; width: 100%; overflow-wrap: break-word; word-wrap: break-word; word-break: break-word;">${ticket.request}</div>
          </div>
        </div>

        <!-- Časování -->
        <div style="background: #fff3cd; border: ${4 * scale}px solid #ffc107; border-radius: ${16 * scale}px; padding: ${35 * scale}px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: ${20 * scale}px; padding-bottom: ${20 * scale}px; border-bottom: ${2 * scale}px solid #ffdb7d;">
            <span style="font-size: ${28 * scale}px; font-weight: bold; color: #856404;">Vydáno:</span>
            <span style="font-size: ${28 * scale}px; color: #856404;">${formatDate(ticket.created_at)}</span>
          </div>
          <div style="background: white; padding: ${30 * scale}px; border-radius: ${12 * scale}px; margin: ${12 * scale}px 0;">
            <div style="text-align: center;">
              <div style="font-size: ${32 * scale}px; font-weight: bold; color: #856404; margin-bottom: ${20 * scale}px;">Datum vyřízení požadavku po schválení nekvalitního zákona o státních zaměstnancích:</div>
              <div style="font-size: ${80 * scale}px; font-weight: bold; color: #d9534f; line-height: 1.2;">${getExpectedDate()}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Patička -->
      <div style="background: #f8f9fa; padding: ${50 * scale}px; text-align: center;">
        <a href="https://www.nechcemestatvefronte.eu" style="margin: 0; font-size: ${56 * scale}px; font-weight: bold; color: #0056b3; text-decoration: none;">www.nechcemestatvefronte.eu</a>
      </div>
    </div>
  `;

  document.body.appendChild(pngElement);

  try {
    // Konverze HTML na canvas
    const canvas = await html2canvas(pngElement, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: dimensions.width,
      height: dimensions.height
    });

    // Konverze canvas na blob a stažení
    canvas.toBlob(async (blob) => {
      const isoString = ticket.created_at.includes('T')
        ? ticket.created_at
        : ticket.created_at.replace(/\s+/, 'T') + 'Z';
      const date = new Date(isoString);
      const dateStr = date.toLocaleDateString('cs-CZ', { timeZone: 'Europe/Prague' }).replace(/\s/g, '').replace(/\./g, '-');
      const timeStr = date.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Prague' }).replace(':', '-');
      const fileName = `listek_${dateStr}_${timeStr}_${ticket.ticket_number}_${aspectRatio.replace(':', '-')}.png`;

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(link.href);

      // Trackovat stažení pouze pokud není admin
      if (shouldTrack) {
        await trackDownload(ticket.id);
      }
    }, 'image/png');
  } finally {
    // Odstranění dočasného elementu
    document.body.removeChild(pngElement);
  }
};

export const generateTicketPDF = async (ticket, aspectRatio = 'A4') => {
  // Aspect ratio dimensions (width x height in mm)
  const aspectRatios = {
    'A4': { width: 210, height: 297, orientation: 'portrait' },
    '4:5': { width: 160, height: 200, orientation: 'portrait' },
    '1:1': { width: 200, height: 200, orientation: 'portrait' },
    '9:16': { width: 168.75, height: 300, orientation: 'portrait' }
  };

  const dimensions = aspectRatios[aspectRatio] || aspectRatios['A4'];

  // Formátování data
  const formatDate = (dateString) => {
    // Pokud už je to ISO formát (obsahuje T), použij přímo
    // Jinak převeď SQLite formát "YYYY-MM-DD HH:MM:SS" na ISO 8601
    const isoString = dateString.includes('T')
      ? dateString
      : dateString.replace(/\s+/, 'T') + 'Z';

    const date = new Date(isoString);

    // Fallback pokud date není validní
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateString, 'converted to:', isoString);
      return 'Neplatné datum';
    }

    return date.toLocaleString('cs-CZ', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Prague'
    });
  };

  const getExpectedDatePDF = () => {
    const isoString = ticket.created_at.includes('T')
      ? ticket.created_at
      : ticket.created_at.replace(/\s+/, 'T') + 'Z';
    const createdDate = new Date(isoString);

    if (isNaN(createdDate.getTime())) {
      return ticket.expected_year;
    }

    const day = createdDate.getDate();
    const month = createdDate.toLocaleString('cs-CZ', { month: 'long', timeZone: 'Europe/Prague' });

    return `${day}. ${month} ${ticket.expected_year}`;
  };

  // Dynamická velikost písma pro PDF - podle délky textu
  const getRequestFontSizePDF = (text, baseSize) => {
    const length = text.length;
    if (length <= 30) return baseSize * 1.5;
    if (length <= 50) return baseSize * 1.4;
    if (length <= 70) return baseSize * 1.4;
    if (length <= 90) return baseSize * 1.35;
    if (length <= 110) return baseSize * 1.3;
    if (length <= 130) return baseSize * 1.2;
    if (length <= 150) return baseSize * 1.1;
    return baseSize * 1.0;
  };


  // Vytvoření HTML elementu pro PDF
  const pdfElement = document.createElement('div');
  pdfElement.style.position = 'absolute';
  pdfElement.style.left = '-9999px';
  pdfElement.style.width = `${dimensions.width}mm`;
  pdfElement.style.background = 'white';
  pdfElement.style.padding = '0';
  pdfElement.style.fontFamily = 'Arial, sans-serif';

  pdfElement.innerHTML = `
    <div style="width: ${dimensions.width}mm; background: white; padding: 0; display: flex; flex-direction: column; min-height: ${dimensions.height}mm;">
      <!-- Tělo ticketu -->
      <div style="padding: 30px; text-align: center; flex: 1; display: flex; flex-direction: column;">
        <!-- Číslo lístku -->
        <div style="background: #f8f9fa; border: 2px dashed #0056b3; border-radius: 8px; padding: 15px 20px; margin-bottom: 25px; text-align: center;">
          <div style="display: block; font-size: 18px; color: #666; margin-bottom: 5px; text-transform: uppercase; letter-spacing: 1px; font-weight: bold;">Pořadové číslo:</div>
          <div style="display: block; font-size: 64px; font-weight: bold; color: #0056b3; font-family: 'Courier New', monospace; margin: 0; line-height: 1;">${'#' + ticket.ticket_number}</div>
        </div>

        <!-- Požadavek -->
        <div style="margin-bottom: 25px; text-align: left; flex: 1; display: flex; flex-direction: column; min-height: 0;">
          <h4 style="font-size: 18px; color: #666; margin: 0 0 15px 0; text-transform: uppercase; font-weight: bold; letter-spacing: 1px;">Co potřebuji od státu:</h4>
          <div style="background: #f8f9fa; padding: 30px; border-radius: 8px; border-left: 4px solid #0056b3; flex: 1; display: flex; align-items: center; justify-content: center; flex-direction: column; min-height: 0; overflow: hidden;">
            <div style="margin: 0; font-size: ${getRequestFontSizePDF(ticket.request, 42)}px; line-height: 1.4; color: #333; font-weight: 600; text-align: left; width: 100%; overflow-wrap: break-word; word-wrap: break-word; word-break: break-word;">${ticket.request}</div>
          </div>
        </div>

        <!-- Časování -->
        <div style="background: #fff3cd; border: 2px solid #ffc107; border-radius: 8px; padding: 25px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #ffdb7d;">
            <span style="font-size: 18px; font-weight: bold; color: #856404;">Vydáno:</span>
            <span style="font-size: 18px; color: #856404;">${formatDate(ticket.created_at)}</span>
          </div>
          <div style="background: white; padding: 20px; border-radius: 6px; margin: 8px 0;">
            <div style="text-align: center;">
              <div style="font-size: 20px; font-weight: bold; color: #856404; margin-bottom: 15px;">Datum vyřízení požadavku po schválení nekvalitního zákona o státních zaměstnancích:</div>
              <div style="font-size: 48px; font-weight: bold; color: #d9534f; line-height: 1.2;">${getExpectedDatePDF()}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Patička -->
      <div style="background: #f8f9fa; padding: 30px; text-align: center;">
        <a href="https://www.nechcemestatvefronte.eu" style="margin: 0; font-size: 28px; font-weight: bold; color: #0056b3; text-decoration: none;">www.nechcemestatvefronte.eu</a>
      </div>
    </div>
  `;

  document.body.appendChild(pdfElement);

  try {
    // Konverze HTML na canvas
    const canvas = await html2canvas(pdfElement, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });

    // Vytvoření PDF
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: dimensions.orientation,
      unit: 'mm',
      format: [dimensions.width, dimensions.height]
    });

    const imgWidth = dimensions.width;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

    // Vytvoření názvu souboru z data a čísla ticketu
    const isoString = ticket.created_at.includes('T')
      ? ticket.created_at
      : ticket.created_at.replace(/\s+/, 'T') + 'Z';
    const date = new Date(isoString);
    const dateStr = date.toLocaleDateString('cs-CZ', { timeZone: 'Europe/Prague' }).replace(/\s/g, '').replace(/\./g, '-');
    const timeStr = date.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Prague' }).replace(':', '-');
    const aspectSuffix = aspectRatio !== 'A4' ? `_${aspectRatio.replace(':', '-')}` : '';
    const fileName = `listek_${dateStr}_${timeStr}_${ticket.ticket_number}${aspectSuffix}.pdf`;

    // Stažení PDF se správným názvem
    pdf.save(fileName);
  } finally {
    // Odstranění dočasného elementu
    document.body.removeChild(pdfElement);
  }
};
